import Head from 'next/head';
import { useRouter } from 'next/router';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkuEnum } from '@/types/translations/SkuEnum';
import { ImagesBlogBlogThumbnailJpeg } from '@/assets/images_blog.assets';
import LanguageAlternates from '../LanguageAlternates';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';

/**
 * Head for the `/tastings` overview.
 *
 * Shorter than `MagazineMeta` on purpose. That component carries a `Blog`
 * JSON-LD block, and the equivalent here would be an `ItemList` of `Event`s —
 * duplicating the `Event` markup each tasting's own page already emits
 * (WEB-1407) and inviting Google to pick the weaker of the two. The listing
 * page's job is to be crawlable and to link; the events describe themselves.
 *
 * The copy lives in `sku.json` with the rest of the tasting strings rather than
 * in `meta.json`, so a tasting's wording is edited in one file.
 */
export default function TastingsMeta(): JSX.Element {
  const router = useRouter();
  const { translate } = useTypedTranslation<SkuEnum>('sku');
  const url = getCanonicalUrl(router);
  const image = `https://spiritory.com${ImagesBlogBlogThumbnailJpeg.src}`;

  const title = translate(SkuEnum['tasting.overview_meta_title']);
  const description = translate(SkuEnum['tasting.overview_meta_description']);

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="image_src" content={image} />
      <meta name="url" content={url} />
      <meta name="identifier-URL" content={url} />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Spiritory" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <link rel="canonical" href={url} />
      <LanguageAlternates />
    </Head>
  );
}
